import { accountMenu } from "@/assets/json/account-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import useAuth from "@/hooks/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import AccountLayout from "@/layouts/AccountLayout";
import {
  CaretRight,
  Pencil,
  SignOut,
  Warning,
} from "@phosphor-icons/react/dist/ssr";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useContext, useEffect, useState } from "react";
import AppContext from "@/AppContext";
import { getWishlist } from "@/services/api/Wishlist";
import { getNotifications } from "@/services/api/Notification";

export default function Account() {
  const { user, logout } = useAuth({ middleware: "auth" });
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [watchlistAmount, setWatchlistAmount] = useState(null);
  const [bookmarkAmount, setBookmarkAmount] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const isMobile = useIsMobile();
  const appContext = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } =
    appContext.state;

  const SubNavigationMobile = dynamic(
    () => import("@/components/elements/SubNavigationMobile"),
    {
      ssr: false,
      loading: () => null,
    }
  );

  const getBookmarkAmount = async () => {
    const res = await getWishlist({
      per_page: 1000,
      type: 0,
    });
    setBookmarkAmount(res.data.data.length);
  };

  const getWatchlistAmount = async () => {
    const res = await getWishlist({
      per_page: 1000,
      excluded_type: 0,
    });
    setWatchlistAmount(res.data.data.length + 1);
  };

  const getNotificationAmount = async () => {
    try {
      const response = await getNotifications();
      if (response?.data?.success) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    getBookmarkAmount();
    getWatchlistAmount();
    getNotificationAmount();
  }, []);

  const filteredMenu = accountMenu.map((item) => {
    // Handle direct Subscription item
    if (item.name === "Subscription") {
      return {
        ...item,
        info:
          planSubscription && planSubscription.has_active_subscription
            ? planSubscription.active_product?.name
            : "Free Plan",
      };
    }
    if (item.name === "Notifications") {
      return {
        ...item,
        info: unreadCount > 0 && unreadCount,
      };
    }
    if (item.children) {
      const updatedChildren = item.children.map((child) => {
        if (child.name === "Bookmarks") {
          return {
            ...child,
            info: bookmarkAmount > 0 ? `${bookmarkAmount} Items` : undefined,
          };
        }
        if (child.name === "Watchlists") {
          return {
            ...child,
            info:
              watchlistAmount > 0
                ? `${watchlistAmount} Collections`
                : undefined,
          };
        }

        return child;
      });

      return {
        ...item,
        children: updatedChildren,
      };
    }
    return item;
  });

  return (
    <AccountLayout title={"Profile | Lucidity Insights"}>
      <div className='container'>
        {isMobile && <SubNavigationMobile label='User Account' />}
        <div className='flex flex-col items-center justify-center text-center'>
          <Avatar className='!size-20'>
            <AvatarImage src={user?.profile_image_url} />
            <AvatarFallback className='text-3xl font-semibold uppercase'>
              {user?.first_name ? user?.first_name[0] : user?.name[0]}
              {user?.last_name ? user?.last_name[0] : ""}
            </AvatarFallback>
          </Avatar>
          <div className='flex flex-col my-3'>
            <span className='text-xl font-bold leading-none'>
              {user?.first_name} {user?.last_name}
            </span>
            <span className='mt-2 text-xs font-normal leading-none text-white/30'>
              {user?.email}
            </span>
          </div>

          <Link
            href='/account/profile'
            className='inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold leading-none tracking-tight bg-white rounded-full text-background'
          >
            <Pencil className='size-3.5 opacity-50' />
            Edit Profile
          </Link>
        </div>

        <div className='flex flex-col gap-2 pb-6 mt-6'>
          {filteredMenu.map((item, index) =>
            item.children ? (
              <div key={`group-${index}`} className='mt-3'>
                {item.category && (
                  <span className='block mb-2 ml-2.5 text-sm font-medium text-white/40'>
                    {item.category}
                  </span>
                )}
                <div className='px-2.5 divide-y rounded-xl divide-dashed divide-white/[0.07] bg-white/5'>
                  {item.children.map((child, childIndex) => (
                    <Link
                      key={`child-${childIndex}`}
                      href={child.url}
                      className='flex items-center gap-3 py-2 font-medium'
                    >
                      <span
                        className={`inline-flex items-center justify-center rounded-lg size-8 bg-gradient-to-tr ${child.color}`}
                      >
                        {child.icon}
                      </span>
                      {child.name}
                      <span className='flex items-center gap-1.5 ml-auto'>
                        <span className='text-xs font-normal text-white/40'>
                          {child.info}
                        </span>
                        <CaretRight
                          weight='bold'
                          className='flex-shrink-0 text-white/20 size-4'
                        />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div
                key={`item-${index}`}
                className='px-2.5 divide-y rounded-xl divide-dashed divide-white/[0.07] bg-white/5'
              >
                <Link
                  href={item.url}
                  className='flex items-center gap-3 py-2 font-medium'
                >
                  <span
                    className={`inline-flex items-center justify-center rounded-lg size-8 bg-gradient-to-tr ${item.color}`}
                  >
                    {item.icon}
                  </span>
                  {item.name}
                  <span className='flex items-center gap-1.5 ml-auto'>
                    <span className='text-xs font-normal truncate whitespace-nowrap text-white/40'>
                      {item.info}
                    </span>
                    <CaretRight
                      weight='bold'
                      className='flex-shrink-0 text-white/20 size-4'
                    />
                  </span>
                </Link>
              </div>
            )
          )}

          <Drawer open={confirmLogout} onOpenChange={setConfirmLogout}>
            <DrawerTrigger asChild>
              <button
                type='button'
                className='flex items-center justify-center gap-3 mt-5 font-medium text-red-500'
              >
                <SignOut weight='fill' className='flex-shrink-0 size-5' />
                Logout
              </button>
            </DrawerTrigger>
            <DrawerContent
              className='p-4 pt-0 text-center bg-white rounded-t-3xl'
              showHandle={false}
            >
              <span className='inline-flex items-center justify-center mx-auto mt-5 rounded-full bg-gradient-to-tr from-red-300 to-red-600 size-12'>
                <Warning weight='fill' className='size-6' />
              </span>
              <p className='mt-4 text-xl font-semibold leading-tight tracking-tight text-background'>
                Wait a sec... Logging out already?
              </p>
              <p className='mt-3 text-sm leading-snug text-background/50'>
                You&apos;ll be signed out and will need to log in again to
                continue. We&apos;ll miss you!
              </p>
              <div className='flex gap-2 mt-6'>
                <Button
                  onClick={() => setConfirmLogout(false)}
                  className='font-semibold bg-gray-100 rounded-lg'
                >
                  Cancel
                </Button>
                <Button
                  onClick={logout}
                  className='flex-1 font-semibold text-white rounded-lg bg-gradient-to-t from-background to-gray-700'
                >
                  Yes, I want to log out
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </AccountLayout>
  );
}

// export async function getServerSideProps() {
//   return {
//     redirect: {
//       destination: "/account/profile",
//       permanent: false,
//     },
//   };
// }
